import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/layout/Header";
import { cn } from "@/lib/utils";

const STATUS_STYLES = {
  new: "bg-gold/15 text-gold-dark",
  contacted: "bg-blue-100 text-blue-800",
  closed: "bg-navy/10 text-navy/60",
};

const FILTERS = ["all", "new", "contacted", "closed"];

export default function AdminDashboard() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem("fc_token");
    navigate("/admin/login", { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/enquiries");
      setItems(data.enquiries);
      setError("");
    } catch (err) {
      if ([401, 403].includes(err?.response?.status)) {
        logout();
      } else {
        setError("Could not load enquiries. Please try again.");
        setItems([]);
      }
    }
  }, [logout]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (err) {
      if ([401, 403].includes(err?.response?.status)) logout();
    }
  };

  const filtered = (items || []).filter((it) => filter === "all" || it.status === filter);
  const counts = FILTERS.reduce((acc, f) => {
    acc[f] = f === "all" ? (items || []).length : (items || []).filter((it) => it.status === f).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-surface" data-testid="admin-dashboard">
      <header className="bg-navy">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
          <Logo light />
          <div className="flex items-center gap-4">
            <span className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-white/50 sm:block">
              Workforce Enquiries
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 border border-white/25 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-gold hover:text-gold"
            >
              <LogOut className="h-3.5 w-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}-btn`}
                className={cn(
                  "border px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-colors",
                  filter === f ? "border-navy bg-navy text-white" : "border-navy/15 bg-white text-charcoal/60 hover:border-navy"
                )}
              >
                {f} <span className="ml-1 text-gold">{counts[f]}</span>
              </button>
            ))}
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh-btn"
            className="inline-flex items-center gap-2 border border-navy/15 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-charcoal/60 transition-colors hover:border-navy"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>

        {error && (
          <p className="mt-6 border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-error">{error}</p>
        )}

        <div className="mt-6 border border-navy/10 bg-white">
          <div className="hidden grid-cols-12 gap-4 border-b border-navy/10 bg-surface px-6 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-charcoal/50 md:grid">
            <span className="col-span-2">Received</span>
            <span className="col-span-3">Contact</span>
            <span className="col-span-2">Company</span>
            <span className="col-span-2">Location</span>
            <span className="col-span-1">Personnel</span>
            <span className="col-span-2">Status</span>
          </div>

          {items === null ? (
            <div className="space-y-px">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-16 animate-pulse border-b border-navy/5 bg-surface/60" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="px-6 py-16 text-center text-sm text-charcoal/50" data-testid="admin-empty-state">
              No enquiries{filter !== "all" ? ` with status "${filter}"` : ""} yet.
            </p>
          ) : (
            filtered.map((item) => (
              <div key={item.id} className="border-b border-navy/5 last:border-0" data-testid={`enquiry-row-${item.id.slice(0, 8)}`}>
                <button
                  onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                  data-testid={`enquiry-expand-${item.id.slice(0, 8)}`}
                  className="grid w-full grid-cols-2 items-center gap-4 px-6 py-4 text-left transition-colors hover:bg-surface/70 md:grid-cols-12"
                >
                  <span className="text-xs text-charcoal/55 md:col-span-2">
                    {new Date(item.created_at).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span className="md:col-span-3">
                    <span className="block text-sm font-bold text-navy">{item.name}</span>
                    <span className="block truncate text-xs text-charcoal/55">{item.email}</span>
                  </span>
                  <span className="hidden text-sm text-charcoal/75 md:col-span-2 md:block">{item.company}</span>
                  <span className="hidden text-sm text-charcoal/75 md:col-span-2 md:block">{item.project_location}</span>
                  <span className="hidden text-sm font-semibold text-navy md:col-span-1 md:block">{item.personnel_count || "—"}</span>
                  <span className="flex items-center justify-between gap-2 md:col-span-2" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={item.status}
                      onChange={(e) => setStatus(item.id, e.target.value)}
                      data-testid={`enquiry-status-${item.id.slice(0, 8)}`}
                      className={cn("border-0 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] outline-none", STATUS_STYLES[item.status] || STATUS_STYLES.new)}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="closed">Closed</option>
                    </select>
                    <ChevronDown className={cn("h-4 w-4 text-charcoal/40 transition-transform", expanded === item.id && "rotate-180")} />
                  </span>
                </button>

                {expanded === item.id && (
                  <div className="border-t border-navy/5 bg-surface/60 px-6 py-6" data-testid={`enquiry-details-${item.id.slice(0, 8)}`}>
                    <div className="grid gap-x-10 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                      <Detail label="Phone" value={item.phone} />
                      <Detail label="Position" value={item.position} />
                      <Detail label="Workforce Required" value={item.workforce_required} />
                      <Detail label="Personnel" value={item.personnel_count} />
                      <Detail label="Expected Start" value={item.start_date} />
                      <Detail label="Duration" value={item.duration} />
                    </div>
                    {item.message && (
                      <div className="mt-5">
                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-charcoal/45">Message</p>
                        <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-charcoal/80">{item.message}</p>
                      </div>
                    )}
                    <p className="mt-5 text-[10px] uppercase tracking-[0.18em] text-charcoal/35">
                      Ref {item.id.slice(0, 8).toUpperCase()}
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-charcoal/45">{label}</p>
      <p className="mt-1 text-charcoal/85">{value || "—"}</p>
    </div>
  );
}
